/**
 * CR150: Hook to fetch the published layout for the public site
 * 
 * This hook specifically loads the "publish" environment layout,
 * which is what end-users see on the live site.
 * 
 * Key behaviors:
 * - Always fetches from environment='publish' (never staging)
 * - Logs errors but uses safe fallback (no silent failures)
 * - Exposes debug info for the debug footer
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { LayoutJson, LayoutPreset, HeaderBlockConfig } from "@/lib/layout-studio/types";
import { DEFAULT_LAYOUT_JSON, DEFAULT_HEADER_CONFIG } from "@/lib/layout-studio/types";
import type { DealCardUXConfig } from "@/lib/layout-studio/dealCardUXTypes";
import { DEFAULT_DEAL_CARD_UX_CONFIG } from "@/lib/layout-studio/dealCardUXTypes";

/** CR201: Header block debug info */
export interface HeaderBlockDebugInfo {
  enabled: boolean;
  titleText: string | null;
  subtitleText: string | null;
  showLogo: boolean;
  showTitle: boolean;
  showSubtitle: boolean;
  logoAssetId: string | null;
}

/** CR201: Block visibility debug info */
export interface BlockDebugInfo {
  id: string;
  enabled: boolean;
  order: number;
}

export interface PublicLayoutDebugInfo {
  environment: "publish";
  activationFound: boolean;
  activePresetId: string | null;
  presetName: string | null;
  activationUpdatedAt: string | null;
  presetUpdatedAt: string | null;
  error: string | null;
  fetchedAt: string;
  usingFallback: boolean;
  /** CR151: Resolved detailsMode for debugging */
  detailsMode: string | null;
  detailsModeSource: "layout" | "fallback" | null;
  /** CR201: Header block debug info */
  headerBlock: HeaderBlockDebugInfo | null;
  /** CR201: All blocks with enabled/order */
  blocks: BlockDebugInfo[];
}

export interface PublicLayoutResult {
  layout: LayoutJson;
  preset: LayoutPreset | null;
  headerConfig: HeaderBlockConfig;
  /** CR151: Deal card UX config (includes detailsMode) */
  dealCardUX: DealCardUXConfig;
  loading: boolean;
  error: string | null;
  debugInfo: PublicLayoutDebugInfo | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch the published layout for the public site.
 * 
 * IMPORTANT: This hook ONLY reads from environment='publish'.
 * It will NEVER fall back to staging data - if publish is missing,
 * it uses DEFAULT_LAYOUT_JSON and logs an error.
 */
export function usePublicLayout(): PublicLayoutResult {
  const [preset, setPreset] = useState<LayoutPreset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<PublicLayoutDebugInfo | null>(null);
  
  const fetchedRef = useRef(false);

  const fetchPublishedLayout = useCallback(async () => {
    setLoading(true);
    setError(null);

    const debugData: PublicLayoutDebugInfo = {
      environment: "publish",
      activationFound: false,
      activePresetId: null,
      presetName: null,
      activationUpdatedAt: null,
      presetUpdatedAt: null,
      error: null,
      fetchedAt: new Date().toISOString(),
      usingFallback: false,
      detailsMode: null,
      detailsModeSource: null,
      headerBlock: null,
      blocks: [],
    };

    try {
      // 1. Get activation record for PUBLISH environment only
      const { data: activation, error: activationError } = await supabase
        .from("deal_finder_layout_activations")
        .select("active_preset_id, updated_at")
        .eq("environment", "publish")
        .maybeSingle();

      if (activationError) {
        console.error("[usePublicLayout] Activation query error:", activationError);
        debugData.error = activationError.message;
        debugData.usingFallback = true;
        setDebugInfo(debugData);
        setError(activationError.message);
        setPreset(null);
        setLoading(false);
        return;
      }

      if (!activation?.active_preset_id) {
        // No preset activated for publish - use fallback
        console.warn("[usePublicLayout] No active preset for publish environment - using fallback");
        debugData.error = "No active preset for publish environment";
        debugData.usingFallback = true;
        setDebugInfo(debugData);
        setPreset(null);
        setLoading(false);
        return;
      }

      debugData.activationFound = true;
      debugData.activePresetId = activation.active_preset_id;
      debugData.activationUpdatedAt = activation.updated_at;

      // 2. Fetch the preset details
      const { data: presetData, error: presetError } = await supabase
        .from("deal_finder_layout_presets")
        .select("*")
        .eq("id", activation.active_preset_id)
        .single();

      if (presetError) {
        console.error("[usePublicLayout] Preset query error:", presetError);
        debugData.error = presetError.message;
        debugData.usingFallback = true;
        setDebugInfo(debugData);
        setError(presetError.message);
        setPreset(null);
        setLoading(false);
        return;
      }

      // Cast the database response to our typed interface
      const typedPreset = {
        ...presetData,
        layout_json: presetData.layout_json as unknown as LayoutJson,
        status: presetData.status as LayoutPreset["status"],
      } as LayoutPreset;

      debugData.presetName = presetData.name;
      debugData.presetUpdatedAt = presetData.updated_at;
      
      setPreset(typedPreset);
      setDebugInfo(debugData);
      
      // CR285: Enhanced logging - log the header title to verify it's loaded correctly
      const layoutJson = presetData.layout_json as unknown as LayoutJson;
      const headerBlock = layoutJson?.blocks?.find(
        (b) => b.id === "header"
      );
      const headerTitle = (headerBlock?.config as HeaderBlockConfig)?.titleText;
      console.log(`[usePublicLayout] Loaded published preset: ${presetData.name} (${activation.active_preset_id})`);
      console.log(`[usePublicLayout] CR285 Layout Version Stamp:`, {
        env: "publish",
        preset_id: activation.active_preset_id,
        preset_name: presetData.name,
        preset_status: presetData.status,
        preset_updated_at: presetData.updated_at,
        header_title: headerTitle || "(not set)",
      });
    } catch (err) {
      console.error("[usePublicLayout] Unexpected error:", err);
      debugData.error = String(err);
      debugData.usingFallback = true;
      setDebugInfo(debugData);
      setError(String(err));
    } finally {
      setLoading(false);
      fetchedRef.current = true;
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    if (!fetchedRef.current) {
      fetchPublishedLayout();
    }
  }, [fetchPublishedLayout]);

  // Derive layout from preset or use fallback
  const layout: LayoutJson = preset?.layout_json || DEFAULT_LAYOUT_JSON;
  
  // Extract header config from layout
  const headerBlock = layout.blocks.find((b) => b.id === "header");
  const headerConfig: HeaderBlockConfig = (headerBlock?.config as HeaderBlockConfig) || DEFAULT_HEADER_CONFIG;
  
  // CR151: Extract dealCardUX config (includes detailsMode, density, cardSize, etc.)
  const dealCardUX: DealCardUXConfig = layout.dealCardUX || DEFAULT_DEAL_CARD_UX_CONFIG;
  
  // CR201: Build block debug info
  const blockDebugInfo: BlockDebugInfo[] = layout.blocks.map((b) => ({
    id: b.id,
    enabled: b.enabled,
    order: b.order,
  }));

  // CR201: Build header block debug info
  const headerBlockDebugInfo: HeaderBlockDebugInfo | null = headerConfig ? {
    enabled: headerBlock?.enabled ?? true,
    titleText: headerConfig.titleText || null,
    subtitleText: headerConfig.subtitleText || null,
    showLogo: headerConfig.showLogo,
    showTitle: headerConfig.showTitle,
    showSubtitle: headerConfig.showSubtitle,
    logoAssetId: headerConfig.logoAssetId || null,
  } : null;

  // Update debug info with resolved detailsMode and block info
  const enrichedDebugInfo: PublicLayoutDebugInfo | null = debugInfo ? {
    ...debugInfo,
    detailsMode: dealCardUX.detailsMode,
    detailsModeSource: layout.dealCardUX ? "layout" : "fallback",
    headerBlock: headerBlockDebugInfo,
    blocks: blockDebugInfo,
  } : null;

  return {
    layout,
    preset,
    headerConfig,
    dealCardUX,
    loading,
    error,
    debugInfo: enrichedDebugInfo,
    refetch: fetchPublishedLayout,
  };
}
